import classNames from 'classnames';
import { Tooltip } from './Tooltip';
import { useT } from '../translation/src';

/**
 * PersonaButton - Icon button for persona selection
 *
 * Displays an icon with a label below it.
 * Used in the renderer header to switch between personas.
 */
interface PersonaButtonProps {
  /** Icon component to display */
  icon: React.FC;
  /** Display name (plain English, used as translation key) */
  name: string;
  /** Whether this persona is currently active */
  active?: boolean;
  /** Whether this persona is disabled */
  disabled?: boolean;
  /** Click handler */
  onClick: () => void;
}

export const PersonaButton: React.FC<PersonaButtonProps> = ({ icon: Icon, name, active = false, disabled = false, onClick }) => {
  // Get translated name for tooltip and aria-label
  const translatedName = useT(name, { swc: true });

  return (
    <Tooltip message={<span>{translatedName}</span>} delay={0}>
      <button
        onClick={onClick}
        disabled={disabled}
        className={classNames(
          'flex items-center justify-center w-10 h-10 rounded-lg transition-all border-2',
          active
            ? 'border-black dark:border-white bg-transparent text-black dark:text-white'
            : disabled
              ? 'border-transparent bg-transparent text-gray-400 cursor-not-allowed opacity-50'
              : 'border-transparent bg-transparent text-black dark:text-white hover:border-gray-400 dark:hover:border-gray-600',
          !disabled && 'cursor-pointer',
        )}
        aria-label={translatedName}
        aria-pressed={active}
      >
        <Icon className="w-5 h-5" />
      </button>
    </Tooltip>
  );
};
